import { handleMouseUpByBlurring } from 'volta/utils/focus';

import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import BvUnstyledLink from 'volta/components/bv-unstyled-link';
import BvIcon from 'volta/components/bv-icon';
import BvTooltip from 'volta/components/bv-tooltip';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import preventDefault from 'volta/helpers/prevent-default';
import eq from 'ember-truth-helpers/helpers/eq';
import classNames from 'volta/helpers/class-names';
import { on } from '@ember/modifier';
import typeOf from 'volta/helpers/typeof';

import type { ContentValue } from '@glint/template';

interface BvResourceListBulkActionsBtnSignature {
  Args: {
    /**
     * Content the action displays
     */
    content?: ContentValue;
    /**
     * Visually hidden text for screen readers
     */
    accessibilityLabel?: string;
    /**
     * A destination to link to, rendered in the action
     */
    url?: string;
    /**
     * Forces url to open in a new tab
     */
    external?: boolean;
    /*
     * Optional icon glyph
     */
    icon?: string;
    /**
     * Optional tooltip text
     */
    tooltip?: string;
    /**
     * Should the action be disabled
     */
    disabled?: boolean;
    /**
     * Should we show a disclosure icon indicating a popover behaviour
     */
    disclosure?: boolean;
    /**
     *
     */
    handleMeasurement?: (width: number) => void;
    /**
     * Callback when an action takes place
     */
    onClick?: (event: MouseEvent) => void;
  };
  Element: HTMLElement;
}

export default class BvResourceListBulkActionsBtnComponent extends Component<BvResourceListBulkActionsBtnSignature> {
  handleMouseUpByBlurring = handleMouseUpByBlurring;

  /**
   * ID used to identify our wrapper element from other instances
   */
  get wrapperId() {
    return guidFor(this);
  }

  @action
  handleDidInsert(/*element*/) {
    if (this.args.handleMeasurement) {
      const bulkActionButtonNode = document.getElementById(this.wrapperId);
      const width =
        (bulkActionButtonNode instanceof Element &&
          bulkActionButtonNode.getBoundingClientRect().width) ||
        0;
      this.args.handleMeasurement?.(width);
    }
  }

  @action
  handleMouseUp(event: MouseEvent) {
    if (!event) {
      return;
    }
    const currentTarget = event.currentTarget as HTMLOrSVGElement | null;
    handleMouseUpByBlurring({ currentTarget });
  }

  @action
  handleClick(event: MouseEvent) {
    return this.args.onClick?.(event);
  }

  <template>
    {{#if @url}}
      <BvUnstyledLink
        @external={{@external}}
        @url={{@url}}
        @onClick={{(preventDefault)}}
        @ariaLabel={{@accessibilityLabel}}
        class='bv-resource-list-bulk-actions__btn'
        {{didInsert this.handleDidInsert}}
        ...attributes
      >
        {{#if @disclosure}}
          <span class='bv-resource-list-bulk-actions__action-content'>
            <span>
              {{@content}}
            </span>
            <span class='bv-resource-list-bulk-actions__action-icon'>
              <BvIcon @glyph='select' @size='sm' />
            </span>
          </span>
        {{else}}
          {{#if (eq (typeOf @icon) 'string')}}
            <BvIcon @glyph={{@icon}} class='u-mr-1' />
          {{else if @icon}}
            <span class='u-mr-1'>{{@icon}}</span>
          {{/if}}
          {{@content}}
        {{/if}}
        {{#if @tooltip}}
          <BvTooltip @text={{@tooltip}} />
        {{/if}}
      </BvUnstyledLink>
    {{else}}
      <button
        id={{this.wrapperId}}
        class={{classNames
          'bv-resource-list-bulk-actions__btn'
          (if @disabled 'bv-resource-list-bulk-actions__btn--disabled')
        }}
        aria-label={{@accessibilityLabel}}
        type='button'
        disabled={{@disabled}}
        {{on 'click' this.handleClick}}
        {{on 'mouseup' this.handleMouseUp}}
        {{didInsert this.handleDidInsert}}
        ...attributes
      >
        {{#if @disclosure}}
          <span class='bv-resource-list-bulk-actions__action-content'>
            <span>
              {{@content}}
            </span>
            <span class='bv-resource-list-bulk-actions__action-icon'>
              <BvIcon @glyph='select' @size='sm' />
            </span>
          </span>
        {{else}}
          {{#if (eq (typeOf @icon) 'string')}}
            <BvIcon @glyph={{@icon}} class='u-mr-1' />
          {{else if @icon}}
            <span class='u-mr-1'>
              {{@icon}}
            </span>
          {{/if}}
          {{@content}}
        {{/if}}
        {{#if @tooltip}}
          <BvTooltip @text={{@tooltip}} />
        {{/if}}
      </button>
    {{/if}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvResourceList::BulkActionBtn': typeof BvResourceListBulkActionsBtnComponent;
    'bv-resource-list/bulk-action-btn': typeof BvResourceListBulkActionsBtnComponent;
  }
}
